/**
 * Redact Page geometry (VE-25621): turns one page's raw MuPDF scan into the
 * boxes that redact every element on it.
 *
 * The render worker's `pageElements` request reads the page's text lines from
 * structured text, its paths and images from a device run (every path with
 * the conservative bound MuPDF's redaction tests — curves and diagonals too),
 * rasterizes it into an ink mask, then calls {@link pageElementBoxes} — IN
 * THE WORKER, so the
 * clustering below never runs on the main thread. Everything here is pure so it
 * is unit-testable without WASM. THIS FILE MUST STAY DEPENDENCY-FREE (type-only
 * imports): the worker bundle imports it at runtime.
 *
 *   1. TEXT  — one box per line, widened to its text block so a paragraph's
 *      short last line still gets a full-width bar, then trimmed so adjacent
 *      lines meet instead of overlapping (an overlap would delete the glyph tops
 *      of a neighbour the user later un-redacts).
 *   2. IMAGES / VECTORS — one padded box per image (its FULL extent, clipped
 *      or not); paths clustered so a table's rules become one table box. A
 *      cluster is never allowed to grow page-sized, and every path's bound
 *      lies wholly inside its cluster's box — burn-in removes a path only when
 *      one box contains all of it (a box fitted to a signature's visible ink
 *      left the curve in the file).
 *   3. RESIDUAL — the guarantee for what is visible. Every ink pixel (any pixel
 *      visibly different from the page background, darker OR lighter) that is
 *      not wholly inside a box from 1–2 gets a residual box: Type3 art,
 *      shadings, glyph pixels an OCR line box misses.
 *
 * A page-sized image (a scan) gets no box of its own — one black box over the
 * page is exactly what VPET-1164 rules out. Instead {@link imageInkBoxes}
 * covers the ink in the image's OWN pixels, so the parts a clip, a covering
 * fill, transparency or the page edge hide are removed too, not only what the
 * render shows. A page-sized single PATH (a border, a background fill) gets no
 * box either and may survive in the content stream under black fills (MuPDF
 * removes only a path one box wholly contains); that is the accepted cost of
 * the line-by-line look.
 *
 * Text lines are never dropped for being thin: a line box under
 * MIN_TEXT_BOX_PT is grown to it, so tiny, zero-width or invisible (render
 * mode 3) text — which leaves no ink for step 3 — is still removed. Text,
 * image and vector boxes are not clamped to the page: text pushed off it is
 * still in the content stream. A path bleeding off the edge is reported
 * only up to it, so burn-in's line-art pass stretches any box reaching the
 * page edge far past it (`stretchPastPageEdges`) to remove the whole path.
 *
 * Not covered here (burn-in limitations shared with manual redactions):
 * non-Redact annotations other than FreeText survive `applyRedactions` and are
 * baked on top; hidden optional-content layers, content outside the CropBox,
 * embedded files and links are not part of the rendered page.
 *
 * All coordinates are PDF points, top-left origin — the `BoundingBox` frame.
 */
import type { BoundingBox, Redaction } from './models';

/** A text block and its lines, as the stext walk reports them. */
export interface ScannedTextBlock {
  bbox: BoundingBox;
  lines: BoundingBox[];
}

/**
 * The rasterized page as an ink mask: `data[y * width + x]` is 1 when that
 * pixel differs visibly from the page background. `pxPerPt` maps it to points.
 */
export interface InkMask {
  width: number;
  height: number;
  pxPerPt: number;
  data: Uint8Array;
}

/** One page's raw scan, as the render worker measures it. */
export interface PageElementScan {
  width: number;
  height: number;
  textBlocks: ScannedTextBlock[];
  /** Every drawn image's FULL extent — unclipped, possibly off the page. */
  images: BoundingBox[];
  /**
   * Boxes over the ink in the PIXELS of each page-sized image (see
   * `imageInkBoxes`) — which, unlike the rendered ink mask, includes the parts
   * of the image a clip, a covering fill or transparency hides.
   */
  imageInk?: BoundingBox[];
  /**
   * Every filled or stroked path's bound, as MuPDF's redaction tests it
   * (`Path.getBounds(stroke, ctm)`: control points and stroke width
   * included) — curves and diagonals too.
   */
  vectors: BoundingBox[];
  ink: InkMask;
}

/** Images / paths / clusters at least this share of the page are backgrounds. */
export const PAGE_SIZED_AREA_RATIO = 0.85;
/** Vector paths closer than this (pt) belong to the same drawing. */
export const VECTOR_CLUSTER_GAP_PT = 2;
/**
 * Image and vector boxes grow by this (pt) per edge. A stroke's paint reaches
 * past the bbox MuPDF reports, and burn-in removes a path only when its box
 * covers it COMPLETELY — an exact box left a 1px colored rim of a chart's
 * legend swatches visible around the black.
 */
export const GRAPHIC_PAD_PT = 1;
/** Residual ink is collected into cells of this size (pt). */
export const INK_CELL_PT = 4;
/**
 * Residual boxes beyond this are coarsened (wider merge reach, looser growth
 * cap) — never past page-sized — while that still reduces them. Speckled scan
 * noise otherwise yields hundreds of one-cell boxes, each a row in the
 * Redactions panel and in the saved asset. The limit is a target, not a
 * guarantee: coverage always wins over the count.
 */
export const RESIDUAL_BOX_LIMIT = 200;
/** Boxes thinner than this (pt) on either side are dropped as degenerate. */
const MIN_BOX_PT = 0.5;
/**
 * A text line box is grown to at least this (pt) on each axis, around its
 * centre. Hidden text — 0.3pt white text, tiny invisible OCR text, `0 Tz`
 * zero-width text — has a near-degenerate line box AND no ink, so dropping the
 * box left it in the export's text layer.
 */
export const MIN_TEXT_BOX_PT = 2;

const area = (b: BoundingBox): number =>
  Math.max(0, b.width) * Math.max(0, b.height);

function union(a: BoundingBox, b: BoundingBox): BoundingBox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    height: Math.max(a.y + a.height, b.y + b.height) - y,
  };
}

function pad(b: BoundingBox, by: number): BoundingBox {
  return {
    x: b.x - by,
    y: b.y - by,
    width: b.width + 2 * by,
    height: b.height + 2 * by,
  };
}

function clampToPage(
  b: BoundingBox,
  width: number,
  height: number
): BoundingBox | null {
  const x = Math.max(0, b.x);
  const y = Math.max(0, b.y);
  const right = Math.min(width, b.x + b.width);
  const bottom = Math.min(height, b.y + b.height);
  if (right - x < MIN_BOX_PT || bottom - y < MIN_BOX_PT) {
    return null;
  }
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * A MuPDF color is white: gray / RGB components all at (or near) 1, or CMYK
 * components all at (or near) 0. The worker drops white vector blocks — a
 * white fill is invisible on the page (typically a background panel behind the
 * content) and would otherwise become one box over everything under it.
 */
export function isWhite(color: ArrayLike<number>): boolean {
  const comps = Array.from(color);
  if (comps.length === 0) {
    return false;
  }
  return comps.length === 4
    ? comps.every((c) => c <= 0.05)
    : comps.every((c) => c >= 0.95);
}

/** A pixel this far (max over channels, of 255) from the background is ink. */
export const INK_SPREAD = 24;

/**
 * Ink mask of an RGB raster (3 bytes per pixel, rows `stride` bytes apart).
 * The background is the page's most common color (quantized to 4 bits per
 * channel, then averaged within that bucket); a pixel is ink when ANY channel
 * differs from it by more than INK_SPREAD, in either direction. Lighter-than-
 * background counts — white text on a dark slide is content — and so does a
 * color of the background's luminance (red on gray).
 */
export function inkMaskFromRgb(
  px: ArrayLike<number>,
  width: number,
  height: number,
  stride: number,
  pxPerPt: number
): InkMask {
  const bucketOf = (i: number): number =>
    ((px[i]! >> 4) << 8) | ((px[i + 1]! >> 4) << 4) | (px[i + 2]! >> 4);
  const counts = new Uint32Array(4096);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      counts[bucketOf(y * stride + x * 3)]! += 1;
    }
  }
  let mode = 0;
  for (let k = 1; k < 4096; k++) {
    if (counts[k]! > counts[mode]!) {
      mode = k;
    }
  }
  let r = 0;
  let g = 0;
  let b = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * stride + x * 3;
      if (bucketOf(i) === mode) {
        r += px[i]!;
        g += px[i + 1]!;
        b += px[i + 2]!;
      }
    }
  }
  const n = Math.max(1, counts[mode]!);
  const bg = [r / n, g / n, b / n] as const;
  const data = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * stride + x * 3;
      if (
        Math.abs(px[i]! - bg[0]) > INK_SPREAD ||
        Math.abs(px[i + 1]! - bg[1]) > INK_SPREAD ||
        Math.abs(px[i + 2]! - bg[2]) > INK_SPREAD
      ) {
        data[y * width + x] = 1;
      }
    }
  }
  return { width, height, pxPerPt, data };
}

export interface ClusterOptions {
  /** Vertical merge reach (pt); defaults to the horizontal `gap`. */
  gapY?: number;
  /**
   * Refuse a merge whose union exceeds this many times the merged boxes'
   * combined area, so scattered marks merge only when that doesn't paint a
   * large empty area.
   */
  maxGrowth?: number;
  /** Refuse a merge whose union would exceed this area (pt²). */
  maxArea?: number;
}

/**
 * Merge boxes that overlap or sit within `gap` (horizontally) / `gapY`
 * (vertically) of each other into their bounding box, honouring the growth and
 * area caps, until a pass merges nothing. Every input box lies wholly inside
 * at least one output box (outputs may overlap where a cap refused a merge).
 *
 * A sort-and-sweep pass over x with union-find, so dense input (thousands of
 * residual runs or vector paths) stays near O(n log n) rather than the O(n³) of
 * restarting a pairwise scan after every merge.
 */
export function clusterBoxes(
  boxes: readonly BoundingBox[],
  gap: number,
  options: ClusterOptions = {}
): BoundingBox[] {
  const gapY = options.gapY ?? gap;
  const maxGrowth = options.maxGrowth ?? Infinity;
  const maxArea = options.maxArea ?? Infinity;
  let current = boxes.map((b) => ({ ...b }));
  for (;;) {
    const order = current
      .map((_, i) => i)
      .sort((a, b) => current[a]!.x - current[b]!.x);
    const parent = current.map((_, i) => i);
    const bbox = current.map((b) => ({ ...b }));
    const summed = current.map(area);
    const find = (start: number): number => {
      let i = start;
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]!]!;
        i = parent[i]!;
      }
      return i;
    };
    let merged = false;
    for (let oi = 0; oi < order.length; oi++) {
      const i = order[oi]!;
      const a = current[i]!;
      const reachRight = a.x + a.width + gap;
      for (let oj = oi + 1; oj < order.length; oj++) {
        const j = order[oj]!;
        const b = current[j]!;
        if (b.x > reachRight) {
          break;
        }
        if (b.y - gapY > a.y + a.height || a.y - gapY > b.y + b.height) {
          continue;
        }
        const ri = find(i);
        const rj = find(j);
        if (ri === rj) {
          continue;
        }
        const u = union(bbox[ri]!, bbox[rj]!);
        const sum = summed[ri]! + summed[rj]!;
        if (area(u) > sum * maxGrowth || area(u) > maxArea) {
          continue;
        }
        parent[rj] = ri;
        bbox[ri] = u;
        summed[ri] = sum;
        merged = true;
      }
    }
    if (!merged) {
      return current;
    }
    const roots = new Set(current.map((_, i) => find(i)));
    current = [...roots].map((r) => bbox[r]!);
  }
}

/** One box per line, spanning its text block's full width. */
export function lineBoxes(blocks: readonly ScannedTextBlock[]): BoundingBox[] {
  const out: BoundingBox[] = [];
  for (const block of blocks) {
    for (const line of block.lines) {
      out.push({
        x: block.bbox.x,
        y: line.y,
        width: block.bbox.width,
        height: line.height,
      });
    }
  }
  return out;
}

/**
 * Where two line boxes overlap vertically (tight leading), move the shared edge
 * to the middle of the overlap so they meet instead of stacking. Coverage of
 * the two line boxes stays complete; any glyph pixel the trim exposes is caught
 * by the residual pass.
 */
export function trimLineOverlaps(lines: readonly BoundingBox[]): BoundingBox[] {
  const out = lines
    .map((b) => ({ ...b }))
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (let i = 0; i < out.length; i++) {
    for (let j = i + 1; j < out.length; j++) {
      const a = out[i]!;
      const b = out[j]!;
      const horizontal = a.x < b.x + b.width && b.x < a.x + a.width;
      const aBottom = a.y + a.height;
      const bBottom = b.y + b.height;
      // Only a partial overlap is trimmed; a line nested inside another
      // (superscript, inline math) is left as is.
      if (!horizontal || b.y >= aBottom || bBottom <= aBottom || b.y <= a.y) {
        continue;
      }
      const cut = (b.y + aBottom) / 2;
      a.height = cut - a.y;
      b.height = bBottom - cut;
      b.y = cut;
    }
  }
  // No degenerate filter here: a thin line is hidden text, grown by the
  // caller (MIN_TEXT_BOX_PT), never dropped.
  return out;
}

/**
 * The INK_CELL_PT cells holding at least one ink pixel that is not wholly
 * inside one of `covered` — exact, no tolerance band: a pixel half outside a
 * box is uncovered. Returns cells as `[col, row]` in a `cols` x `rows` grid.
 */
export function uncoveredInkCells(
  ink: InkMask,
  covered: readonly BoundingBox[]
): { cols: number; rows: number; cells: Uint8Array } {
  const { width, height, pxPerPt, data } = ink;
  // Pixels wholly inside some covered box, painted once per box.
  const inside = new Uint8Array(width * height);
  for (const b of covered) {
    const x0 = Math.max(0, Math.ceil(b.x * pxPerPt));
    const y0 = Math.max(0, Math.ceil(b.y * pxPerPt));
    const x1 = Math.min(width, Math.floor((b.x + b.width) * pxPerPt));
    const y1 = Math.min(height, Math.floor((b.y + b.height) * pxPerPt));
    for (let y = y0; y < y1; y++) {
      inside.fill(1, y * width + x0, y * width + Math.max(x0, x1));
    }
  }
  const cellPx = INK_CELL_PT * pxPerPt;
  const cols = Math.max(1, Math.ceil(width / cellPx));
  const rows = Math.max(1, Math.ceil(height / cellPx));
  const cells = new Uint8Array(cols * rows);
  // A pixel spans [x, x+1) px; at a non-integer px/pt it can straddle a cell
  // edge, so mark EVERY cell it touches, not just the one holding its corner.
  const lastCell = (edge: number, count: number): number =>
    Math.min(count - 1, Math.ceil(edge / cellPx) - 1);
  for (let y = 0; y < height; y++) {
    const row0 = Math.min(rows - 1, Math.floor(y / cellPx));
    const row1 = Math.max(row0, lastCell(y + 1, rows));
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (data[i] && !inside[i]) {
        const col0 = Math.min(cols - 1, Math.floor(x / cellPx));
        const col1 = Math.max(col0, lastCell(x + 1, cols));
        for (let row = row0; row <= row1; row++) {
          for (let col = col0; col <= col1; col++) {
            cells[row * cols + col] = 1;
          }
        }
      }
    }
  }
  return { cols, rows, cells };
}

/**
 * Residual cells as rectangles: horizontal runs per row, stacked down while
 * the next row has a run with the same span, then clustered where that adds
 * little empty area — across word gaps of up to three cells, but vertically
 * only where the ink touches, so a scan with no text layer still comes out
 * close to line by line. Every cell is covered exactly; no box is allowed to
 * grow page-sized, so a page border yields four thin edges and a speckled scan
 * stays many boxes rather than one black page.
 */
export function residualInkBoxes(
  grid: { cols: number; rows: number; cells: Uint8Array },
  pageWidth: number,
  pageHeight: number
): BoundingBox[] {
  const rects = gridRuns(grid).map((r) => ({
    x: r.c0 * INK_CELL_PT,
    y: r.r0 * INK_CELL_PT,
    width: (r.c1 - r.c0) * INK_CELL_PT,
    height: (r.r1 - r.r0) * INK_CELL_PT,
  }));
  return coarsenResidual(rects, pageWidth, pageHeight);
}

/** A rectangle of grid cells: columns [c0, c1), rows [r0, r1). */
interface CellRect {
  c0: number;
  c1: number;
  r0: number;
  r1: number;
}

/**
 * Set cells as rectangles: horizontal runs per row, stacked down while the
 * next row has a run with the same span. Exact cover of the set cells.
 */
function gridRuns(grid: {
  cols: number;
  rows: number;
  cells: Uint8Array;
}): CellRect[] {
  const { cols, rows, cells } = grid;
  let open = new Map<string, CellRect>();
  const done: CellRect[] = [];
  for (let row = 0; row < rows; row++) {
    const next = new Map<string, CellRect>();
    let col = 0;
    while (col < cols) {
      if (!cells[row * cols + col]) {
        col++;
        continue;
      }
      const start = col;
      while (col < cols && cells[row * cols + col]) {
        col++;
      }
      const key = `${start}:${col}`;
      const above = open.get(key);
      if (above) {
        above.r1 = row + 1;
        next.set(key, above);
        open.delete(key);
      } else {
        next.set(key, { c0: start, c1: col, r0: row, r1: row + 1 });
      }
    }
    done.push(...open.values());
    open = next;
  }
  done.push(...open.values());
  return done;
}

/**
 * Cluster residual rectangles where that adds little empty area — across word
 * gaps of up to three cells, but vertically only where they touch — then, past
 * RESIDUAL_BOX_LIMIT, with a wider reach. Never page-sized.
 */
function coarsenResidual(
  rects: BoundingBox[],
  pageWidth: number,
  pageHeight: number
): BoundingBox[] {
  const cellPt = INK_CELL_PT;
  const maxArea = pageWidth * pageHeight * PAGE_SIZED_AREA_RATIO;
  let out = clusterBoxes(rects, 3 * cellPt, { gapY: 0, maxGrowth: 2, maxArea });
  for (
    let reach = 6 * cellPt, growth = 3;
    out.length > RESIDUAL_BOX_LIMIT && reach <= 32 * cellPt;
    reach *= 2, growth += 1
  ) {
    const coarser = clusterBoxes(out, reach, { maxGrowth: growth, maxArea });
    if (coarser.length === out.length) {
      break;
    }
    out = coarser;
  }
  return out;
}

/**
 * Boxes over the ink in an IMAGE's own pixels (RGB, 3 bytes per pixel, rows
 * `stride` bytes apart), mapped onto the page through the image's `ctm`
 * ([a, b, c, d, e, f]: pixel column i / row j sits at unit (u, v) = (i / w,
 * j / h), and unit (u, v) lands on the page at (a·u + c·v + e, b·u + d·v + f)).
 *
 * For page-sized images only — the ones that get no box of their own. The
 * rendered ink mask sees just what is VISIBLE of them; a scan cropped by a
 * clip, painted over by a white fill, drawn nearly transparent or reaching off
 * the page keeps the rest of its pixels in the file, extractable with
 * `pdfimages`. Burn-in removes image pixels under a box, so every inked pixel
 * gets one, unclamped. Rotated images are covered by each cell block's
 * bounding box (conservative).
 */
export function imageInkBoxes(
  px: ArrayLike<number>,
  width: number,
  height: number,
  stride: number,
  ctm: readonly number[],
  pageWidth: number,
  pageHeight: number
): BoundingBox[] {
  if (width <= 0 || height <= 0) {
    return [];
  }
  const [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0] = ctm;
  const ink = inkMaskFromRgb(px, width, height, stride, 1);
  // Pixels per INK_CELL_PT on each image axis.
  const ptPerCol = Math.hypot(a, b) / width;
  const ptPerRow = Math.hypot(c, d) / height;
  const bw = Math.max(1, Math.floor(INK_CELL_PT / Math.max(ptPerCol, 1e-9)));
  const bh = Math.max(1, Math.floor(INK_CELL_PT / Math.max(ptPerRow, 1e-9)));
  const cols = Math.ceil(width / bw);
  const rows = Math.ceil(height / bh);
  const cells = new Uint8Array(cols * rows);
  for (let y = 0; y < height; y++) {
    const row = Math.floor(y / bh);
    for (let x = 0; x < width; x++) {
      if (ink.data[y * width + x]) {
        cells[row * cols + Math.floor(x / bw)] = 1;
      }
    }
  }
  const toPage = (u: number, v: number): [number, number] => [
    a * u + c * v + e,
    b * u + d * v + f,
  ];
  const rects = gridRuns({ cols, rows, cells }).map((r) => {
    const u0 = (r.c0 * bw) / width;
    const u1 = Math.min(1, (r.c1 * bw) / width);
    const v0 = (r.r0 * bh) / height;
    const v1 = Math.min(1, (r.r1 * bh) / height);
    const corners = [
      toPage(u0, v0),
      toPage(u1, v0),
      toPage(u0, v1),
      toPage(u1, v1),
    ];
    const xs = corners.map((p) => p[0]);
    const ys = corners.map((p) => p[1]);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return pad(
      { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y },
      GRAPHIC_PAD_PT
    );
  });
  return coarsenResidual(rects, pageWidth, pageHeight);
}

/**
 * Every box that redacts the scanned page: text lines, images, vector
 * clusters, then residual boxes for any ink left uncovered. Clamped to the
 * page; degenerate boxes dropped.
 */
export function pageElementBoxes(scan: PageElementScan): BoundingBox[] {
  const pageArea = scan.width * scan.height;
  const sizedLimit = pageArea * PAGE_SIZED_AREA_RATIO;
  const isPageSized = (b: BoundingBox): boolean =>
    pageArea > 0 && area(b) >= sizedLimit;
  const clamp = (boxes: BoundingBox[]): BoundingBox[] =>
    boxes.flatMap((b) => clampToPage(b, scan.width, scan.height) ?? []);
  const atLeast = (b: BoundingBox): BoundingBox => {
    const w = Math.max(b.width, MIN_TEXT_BOX_PT);
    const h = Math.max(b.height, MIN_TEXT_BOX_PT);
    return {
      x: b.x + (b.width - w) / 2,
      y: b.y + (b.height - h) / 2,
      width: w,
      height: h,
    };
  };

  // Grown before the trim (so a thin line takes part in it) and after (so the
  // trim can't shrink one back under the minimum). NOT clamped: text pushed
  // off the page (a large negative Tw / Tc) is still in the content stream.
  const lines = trimLineOverlaps(lineBoxes(scan.textBlocks).map(atLeast)).map(
    atLeast
  );
  const images = scan.images
    .filter((b) => !isPageSized(b))
    .map((b) => pad(b, GRAPHIC_PAD_PT));
  // Clustered from PADDED paths, so each padded path lies wholly inside its
  // cluster's box; capped below page size so a page-wide table or form splits
  // into several boxes instead of one black page.
  const vectors = clusterBoxes(
    scan.vectors
      .filter((b) => !isPageSized(b))
      .map((b) => pad(b, GRAPHIC_PAD_PT)),
    VECTOR_CLUSTER_GAP_PT,
    { maxArea: sizedLimit }
  );
  // Lines, images, vectors and image ink stay UNCLAMPED (see header).
  const elements = [...lines, ...images, ...vectors, ...(scan.imageInk ?? [])];
  const residual = residualInkBoxes(
    uncoveredInkCells(scan.ink, elements),
    scan.width,
    scan.height
  );
  return dropContained([...elements, ...clamp(residual)]);
}

/**
 * Drop every box wholly inside another (keeping one of exact duplicates).
 * Nothing is lost: whatever the inner box covers or removes, the outer one
 * does too. Keeps stacked duplicates (a PDF with a thousand copies of one
 * image) from becoming a thousand redaction rows — and a thousand burn-in
 * line-art layers.
 */
export function dropContained<T extends BoundingBox>(boxes: readonly T[]): T[] {
  const inside = (a: BoundingBox, b: BoundingBox): boolean =>
    a.x >= b.x &&
    a.y >= b.y &&
    a.x + a.width <= b.x + b.width &&
    a.y + a.height <= b.y + b.height;
  // Largest first, so a box is only ever tested against bigger survivors.
  const order = boxes
    .map((b, i) => ({ b, i }))
    .sort((p, q) => area(q.b) - area(p.b) || p.i - q.i);
  const kept: Array<{ b: T; i: number }> = [];
  for (const p of order) {
    if (!kept.some((k) => inside(p.b, k.b))) {
      kept.push(p);
    }
  }
  return kept.sort((p, q) => p.i - q.i).map((k) => k.b);
}

/**
 * Share of `box` covered by the union of `cover` (0..1). Exact, via coordinate
 * compression over the cover boxes clipped to `box`.
 */
export function coveredFraction(
  box: BoundingBox,
  cover: readonly BoundingBox[]
): number {
  const total = area(box);
  if (total <= 0) {
    return 1;
  }
  const right = box.x + box.width;
  const bottom = box.y + box.height;
  const clipped = cover
    .map((c) => ({
      x0: Math.max(box.x, c.x),
      y0: Math.max(box.y, c.y),
      x1: Math.min(right, c.x + c.width),
      y1: Math.min(bottom, c.y + c.height),
    }))
    .filter((c) => c.x1 > c.x0 && c.y1 > c.y0);
  if (clipped.length === 0) {
    return 0;
  }
  const xs = [...new Set(clipped.flatMap((c) => [c.x0, c.x1]))].sort(
    (a, b) => a - b
  );
  const ys = [...new Set(clipped.flatMap((c) => [c.y0, c.y1]))].sort(
    (a, b) => a - b
  );
  let coveredArea = 0;
  for (let i = 0; i + 1 < xs.length; i++) {
    const cx = (xs[i]! + xs[i + 1]!) / 2;
    for (let j = 0; j + 1 < ys.length; j++) {
      const cy = (ys[j]! + ys[j + 1]!) / 2;
      if (
        clipped.some((c) => c.x0 <= cx && cx < c.x1 && c.y0 <= cy && cy < c.y1)
      ) {
        coveredArea += (xs[i + 1]! - xs[i]!) * (ys[j + 1]! - ys[j]!);
      }
    }
  }
  return coveredArea / total;
}

/**
 * Drop candidates ENTIRELY under `existing` — and nothing less: overlapping
 * black boxes are harmless, an uncovered sliver is not. The caller passes only
 * plain rectangles that remove exactly what they show (active manual boxes),
 * never detections or glyph-anchored boxes, whose cover can be switched off or
 * is inset at burn-in.
 */
export function dedupeAgainstExisting(
  candidates: readonly BoundingBox[],
  existing: readonly BoundingBox[]
): BoundingBox[] {
  return candidates.filter((c) => coveredFraction(c, existing) < 1 - 1e-9);
}

/**
 * The boxes as ordinary rectangular `manual` redactions, tied together by one
 * `pageRedactionId` (metadata only — each stays individually removable).
 */
export function pageElementRedactions(
  page: number,
  boxes: readonly BoundingBox[],
  pageRedactionId: string,
  newId: () => string
): Redaction[] {
  return boxes.map((geometry) => ({
    id: newId(),
    origin: 'manual',
    page,
    geometry: { ...geometry },
    pageRedactionId,
  }));
}
